import { Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';

type CacheEntry<T> = {
  value$: Observable<T>;
  expiresAt: number;
};

/**
 * Small in-memory cache for HTTP-backed Observables.
 *
 * - Dedupes concurrent callers (same key shares the same request)
 * - TTL-based to avoid staleness
 * - Memory-only (clears on refresh)
 */
@Injectable({ providedIn: 'root' })
export class ApiCacheService {
  private cache = new Map<string, CacheEntry<unknown>>();

  /**
   * Get a cached observable for `key` or create + cache it.
   *
   * @param key Unique cache key
   * @param factory Factory that returns an Observable (usually HttpClient call)
   * @param ttlMs Time-to-live in ms (default 30s)
   */
  get<T>(key: string, factory: () => Observable<T>, ttlMs = 30_000): Observable<T> {
    const now = Date.now();
    const existing = this.cache.get(key) as CacheEntry<T> | undefined;

    if (existing && existing.expiresAt > now) {
      return existing.value$;
    }

    const value$ = factory().pipe(shareReplay({ bufferSize: 1, refCount: true }));
    this.cache.set(key, { value$, expiresAt: now + ttlMs });
    return value$;
  }

  /** Remove a single key */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /** Clear all cached observables */
  clear(): void {
    this.cache.clear();
  }
}
